import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Crown, Check, Loader2, Gift, Ticket, Lock } from "lucide-react";
import { startCheckout, redeemCode } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const PERKS = [
  "Unlimited lectures every month",
  "AI voice narration for every lecture",
  "Ask the Professor — your grounded AI tutor",
  "Focus Mode: fix your weak spots",
  "Side-by-side bilingual view",
  "10-minute quick-study sessions",
];

export const UpgradeModal = ({ open, onClose }) => {
  const { refresh } = useAuth();
  const [plan, setPlan] = useState("pro_yearly");
  const [gift, setGift] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  const redeem = async () => {
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      await redeemCode(code.trim());
      await refresh();
      toast.success("Code redeemed — welcome to Pro!");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setRedeeming(false);
    }
  };

  const giftValid = !gift || !!recipient.trim();

  const checkout = async () => {
    if (!giftValid) { toast.error("Enter the recipient's email."); return; }
    setCheckingOut(true);
    try {
      const { checkout_url } = await startCheckout(window.location.origin, plan, {
        is_gift: gift,
        recipient_email: recipient.trim(),
      });
      if (!checkout_url) throw new Error("No checkout URL");
      window.location.href = checkout_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start checkout. Please try again.";
      toast.error(msg);
      setCheckingOut(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">Upgrade to Lectura Pro</DialogTitle>
        <DialogDescription className="sr-only">Choose a monthly or yearly Lectura Pro pass and pay securely with card via Stripe, or gift Pro to someone else.</DialogDescription>
        <div className="bg-primary text-primary-foreground p-6 text-center">
          <div className="grid place-items-center h-14 w-14 mx-auto rounded-2xl bg-primary-foreground/15">
            <Crown className="h-7 w-7" />
          </div>
          <h2 className="mt-4 font-serif text-3xl font-bold">Lectura Pro</h2>
          <p className="mt-1 opacity-70 text-sm">Unlock unlimited studying — a one-time pass, no auto-renewal.</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-3 mb-5">
            <button
              data-testid="plan-yearly"
              onClick={() => setPlan("pro_yearly")}
              className={`relative rounded-2xl border-2 p-4 text-left transition ${plan === "pro_yearly" ? "border-accent bg-accent/10" : "border-border"}`}
            >
              <span className="absolute -top-2 right-3 rounded-full bg-accent text-accent-foreground text-[10px] font-bold px-2 py-0.5">BEST VALUE</span>
              <p className="font-serif text-2xl font-bold">$44.99</p>
              <p className="text-xs text-muted-foreground">1 year · ~$3.75/mo</p>
            </button>
            <button
              data-testid="plan-monthly"
              onClick={() => setPlan("pro_monthly")}
              className={`rounded-2xl border-2 p-4 text-left transition ${plan === "pro_monthly" ? "border-accent bg-accent/10" : "border-border"}`}
            >
              <p className="font-serif text-2xl font-bold">$7.99</p>
              <p className="text-xs text-muted-foreground">30 days</p>
            </button>
          </div>
          <button
            data-testid="gift-toggle"
            onClick={() => setGift((g) => !g)}
            className={`w-full flex items-center gap-2 rounded-xl border-2 px-4 py-2.5 text-sm font-semibold mb-3 transition ${gift ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground"}`}
          >
            <Gift className="h-4 w-4" /> {gift ? "Gifting to someone else" : "Buy this as a gift"}
          </button>
          {gift && (
            <input
              data-testid="gift-email"
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="Recipient's email"
              className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring/60 mb-4"
            />
          )}
          <ul className="space-y-3 mb-5">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm">
                <span className="grid place-items-center h-5 w-5 rounded-full bg-accent/15 text-accent shrink-0 mt-0.5">
                  <Check className="h-3.5 w-3.5" />
                </span>
                {p}
              </li>
            ))}
          </ul>

          <button
            data-testid="stripe-checkout-btn"
            onClick={checkout}
            disabled={checkingOut || !giftValid}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-accent text-accent-foreground py-3.5 font-semibold hover:opacity-90 transition disabled:opacity-60"
          >
            {checkingOut ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting to secure checkout…</>
            ) : (
              <><Lock className="h-4 w-4" /> Continue to secure checkout</>
            )}
          </button>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            Card checkout via Stripe · one-time pass, no auto-renewal · phone support available at 1-888-926-2289
          </p>

          <div className="mt-4 pt-4 border-t border-border">
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
              <Ticket className="h-3.5 w-3.5" /> Have a code?
            </label>
            <div className="mt-2 flex gap-2">
              <input
                data-testid="redeem-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="LECT-XXXX-XXXX"
                className="flex-1 rounded-xl border border-input bg-background px-4 py-2.5 text-sm font-mono uppercase outline-none focus:ring-2 focus:ring-ring/60"
              />
              <button
                data-testid="redeem-code-btn"
                onClick={redeem}
                disabled={redeeming || !code.trim()}
                className="rounded-xl bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition"
              >
                {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redeem"}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
